// The <i3-chat-widget> custom element signs users in by navigating the whole portal to its
// SSO bootstrap endpoint and back. This module swaps that navigation for a popup window so the
// user never loses the page they are on, and closes the popup once the session exists.

// Must stay in sync with public/ai-chat-auth-callback.js.
const AI_CHAT_AUTH_COMPLETE_MESSAGE = 'i3-chat-auth-complete';
const CALLBACK_FILE = 'ai-chat-auth-callback.html';
const POPUP_NAME = 'i3-chat-widget-login';
// Large enough for the SSO credential and second-factor forms, not just a silent redirect.
const POPUP_WIDTH = 560;
const POPUP_HEIGHT = 760;
const POPUP_POLL_INTERVAL_MS = 500;
const POPUP_TIMEOUT_MS = 5 * 60 * 1000;

// Internals of the third-party widget that the popup flow has to drive.
type I3ChatWidgetLoginInternals = {
  _endpointBase: string;
  _redirectToLogin(question?: string): boolean;
  _storageKey(suffix: string): string;
  _resumePendingQuestion?(): void;
  _getAuthPreflight?(): Promise<boolean>;
  _authPreflight: Promise<boolean> | null;
  _authVerified: boolean;
  _configCache: unknown;
};

function buildCallbackUrl(appBaseUrl: string): string {
  const base = appBaseUrl.endsWith('/') ? appBaseUrl : `${appBaseUrl}/`;
  return new URL(CALLBACK_FILE, base).toString();
}

function buildPopupFeatures(): string {
  const screenLeft = window.screenLeft ?? window.screenX ?? 0;
  const screenTop = window.screenTop ?? window.screenY ?? 0;
  const outerWidth = window.outerWidth || document.documentElement.clientWidth;
  const outerHeight =
    window.outerHeight || document.documentElement.clientHeight;
  const left = Math.max(
    0,
    Math.round(screenLeft + (outerWidth - POPUP_WIDTH) / 2),
  );
  const top = Math.max(
    0,
    Math.round(screenTop + (outerHeight - POPUP_HEIGHT) / 2),
  );

  return `popup=yes,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable=yes,scrollbars=yes`;
}

function setSessionItem(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode, blocked cookies); losing the question is fine.
  }
}

// Resolves once the popup reports back, is closed by the user, or the flow times out.
function waitForPopupResult(
  popup: Window,
  callbackOrigin: string,
): Promise<void> {
  return new Promise<void>(resolve => {
    const listeners = new AbortController();
    let pollId = 0;
    let timeoutId = 0;

    const finish = () => {
      listeners.abort();
      window.clearInterval(pollId);
      window.clearTimeout(timeoutId);
      resolve();
    };

    window.addEventListener(
      'message',
      event => {
        if (event.origin !== callbackOrigin || event.source !== popup) {
          return;
        }
        const data = event.data as { type?: string } | null;
        if (data?.type !== AI_CHAT_AUTH_COMPLETE_MESSAGE) {
          return;
        }
        finish();
      },
      { signal: listeners.signal },
    );
    pollId = window.setInterval(() => {
      if (popup.closed) {
        finish();
      }
    }, POPUP_POLL_INTERVAL_MS);
    timeoutId = window.setTimeout(finish, POPUP_TIMEOUT_MS);
  });
}

// Drops the widget's cached "unauthenticated" state so it picks up the session created in the popup.
async function revalidateWidgetAuth(
  widget: I3ChatWidgetLoginInternals,
): Promise<boolean> {
  widget._configCache = null;
  widget._authPreflight = null;
  widget._authVerified = false;
  return (await widget._getAuthPreflight?.()) ?? false;
}

async function runPopupLogin(
  widget: I3ChatWidgetLoginInternals,
  appBaseUrl: string,
  fallbackRedirect: (question?: string) => boolean,
  question?: string,
): Promise<void> {
  const callbackUrl = buildCallbackUrl(appBaseUrl);
  const bootstrapUrl = `${
    widget._endpointBase
  }/app/auth/bootstrap?return_to=${encodeURIComponent(callbackUrl)}`;

  const popup = window.open(bootstrapUrl, POPUP_NAME, buildPopupFeatures());
  if (!popup) {
    // Popups are blocked, so keep the widget's original full-page redirect as a fallback.
    fallbackRedirect.call(widget, question);
    return;
  }
  popup.focus();

  try {
    await waitForPopupResult(popup, new URL(callbackUrl).origin);
  } finally {
    if (!popup.closed) {
      popup.close();
    }
  }

  const authenticated = await revalidateWidgetAuth(widget);
  if (authenticated && question) {
    setSessionItem(widget._storageKey('pending-question'), question);
    widget._resumePendingQuestion?.();
  }
}

/**
 * Replaces the widget's page-level SSO redirect with a popup, and returns a cleanup function
 * that restores the original behaviour.
 */
export function installAiChatWidgetPopupLogin(
  element: HTMLElement,
  appBaseUrl: string,
): () => void {
  const widget = element as unknown as I3ChatWidgetLoginInternals;
  const originalRedirectToLogin = widget._redirectToLogin;
  if (typeof originalRedirectToLogin !== 'function') {
    return () => {};
  }

  let running: Promise<void> | undefined;

  widget._redirectToLogin = (question?: string) => {
    if (!running) {
      running = runPopupLogin(
        widget,
        appBaseUrl,
        originalRedirectToLogin,
        question,
      ).finally(() => {
        running = undefined;
      });
    }
    return true;
  };

  return () => {
    widget._redirectToLogin = originalRedirectToLogin;
  };
}
